/**
 * Cart Controller
 *
 * Centralized cart state management and Shopify Cart API integration.
 * Handles all cart operations with proper error handling and event communication.
 * Uses direct event delegation to handle add to cart and buy now buttons globally.
 *
 * Features:
 * - Add items to cart via buttons or form submission
 * - Buy now functionality with cart redirect
 * - Update item quantities with debouncing
 * - Remove items from cart
 * - Cart note management
 * - Stock availability error handling
 * - Cart state synchronization
 * - Multi-button state management (disables all related buttons during operations)
 *
 * Events Emitted:
 * - cart:loading { variantId, action, buyNow }
 * - cart:updated { cart, count, action, variantId, key, buyNow }
 * - cart:error { message, action, variantId, key }
 * - cart:item-loading { key, isLoading }
 * - cart:note-saving
 * - cart:note-saved { cart }
 * - cart:toggle - Request to toggle cart visibility
 *
 * Events Listened:
 * - cart:action { action, variantId, quantity, buyNow } - Legacy event support
 *
 * Data Attributes:
 * - [data-add-to-cart] - Add to cart button (handles globally)
 * - [data-buynow="true"] - Buy now button flag
 * - [data-cart-item-key] - Cart item container with key
 * - [data-cart-item-qty] - Quantity input
 * - [data-cart-item-remove] - Remove button
 * - [data-cart-qty-increase] - Increase quantity button
 * - [data-cart-qty-decrease] - Decrease quantity button
 * - [data-cart-note] - Cart note textarea
 *
 * API Endpoints:
 * - GET /cart.js - Get cart
 * - POST /cart/add.js - Add item
 * - POST /cart/change.js - Update quantity
 * - POST /cart/update.js - Update cart properties (note)
 *
 * Locale Strings Used:
 * - window.themeStrings.cart.adding - Adding to cart text
 * - window.themeStrings.cart.added - Successfully added text
 * - window.themeStrings.cart.redirecting - Redirecting text for buy now
 * - window.themeStrings.cart.error - Error state text
 * - window.themeStrings.cart.buy_now - Buy now button text
 * - window.themeStrings.cart.add_to_cart - Add to cart button text
 * - window.themeStrings.cart.stock_error - Stock limitation error message
 * - window.themeStrings.cart.out_of_stock - Out of stock message
 * - window.themeStrings.cart.update_error - Generic update error message
 *
 * Settings Used:
 * - window.themeSettings.product.openCartOnAdd - Auto-open cart on add
 */

class CartController extends HTMLElement {
  constructor() {
    super();

    this._quantityTimers = {};
    this._pendingDeltas = {};
    this._noteTimer = null;

    // Save bound references for proper cleanup
    this._boundOnCartAction = this._onCartAction.bind(this);
    this._boundHandleInput = this._handleDelegatedInput.bind(this);
    this._boundHandleChange = this._handleDelegatedChange.bind(this);
    this._boundHandleClick = this._handleDelegatedClick.bind(this);
    this._boundHandleSubmit = this._handleDelegatedSubmit.bind(this);
    this._boundGlobalSubmit = this._onGlobalSubmit.bind(this);
  }

  connectedCallback() {
    // Listen for cart actions
    document.addEventListener("cart:action", this._boundOnCartAction);

    // Event delegation for cart UI elements
    this.addEventListener("input", this._boundHandleInput);
    this.addEventListener("change", this._boundHandleChange);
    document.addEventListener("click", this._boundHandleClick);
    this.addEventListener("submit", this._boundHandleSubmit);

    // Global event for forms
    document.addEventListener("submit", this._boundGlobalSubmit);
  }

  disconnectedCallback() {
    document.removeEventListener("cart:action", this._boundOnCartAction);
    this.removeEventListener("input", this._boundHandleInput);
    this.removeEventListener("change", this._boundHandleChange);
    document.removeEventListener("click", this._boundHandleClick);
    this.removeEventListener("submit", this._boundHandleSubmit);
    document.removeEventListener("submit", this._boundGlobalSubmit);
  }

  /* ===========================================
   * EVENT DELEGATION
   * =========================================== */

  _handleDelegatedInput(e) {
    const target = e.target;

    // Quantity input with debouncing
    if (target.matches("[data-cart-item-qty]")) {
      this._onQuantityInput(target);
    }

    // Cart note
    if (target.matches("[data-cart-note]")) {
      this._onNoteInput(target);
    }
  }

  _handleDelegatedChange(e) {
    const target = e.target;

    // Cart note on change (for manual save trigger)
    if (target.matches("[data-cart-note]")) {
      this._saveNote(target.value);
    }
  }

  _handleDelegatedClick(e) {
    const target = e.target.closest("button, a");
    if (!target) return;

    // Add to cart button
    if (target.matches("[data-add-to-cart]")) {
      e.preventDefault();
      this._handleAddToCart(target);
      return;
    }

    // Get cart item container and key
    const itemContainer = target.closest("[data-cart-item-key]");
    if (!itemContainer) return;

    const key = itemContainer.dataset.cartItemKey;
    if (!key) return;

    // Remove item
    if (target.matches("[data-cart-item-remove]")) {
      e.preventDefault();
      this._removeItem(key);
    }

    // Quantity increase
    if (target.matches("[data-cart-qty-increase]")) {
      e.preventDefault();
      this._adjustQuantity(key, 1);
    }

    // Quantity decrease
    if (target.matches("[data-cart-qty-decrease]")) {
      e.preventDefault();
      this._adjustQuantity(key, -1);
    }
  }

  async _handleAddToCart(button) {
    // Find the product controller
    const productController = button.closest("product-controller");
    if (!productController) {
      console.warn("Add to cart button not inside product-controller");
      return;
    }

    // Get variant ID from hidden input
    const variantInput = productController.querySelector('input[name="id"]');
    const variantId = variantInput?.value;

    if (!variantId) {
      console.warn("No variant selected");
      return;
    }

    // Get quantity
    const quantityInput = productController.querySelector(
      'input[name="quantity"]',
    );
    const quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

    // Check if buy now
    const isBuyNow = button.dataset.buynow === "true";

    // Use shared add to cart logic
    await this._performAddToCart(
      productController,
      variantId,
      quantity,
      isBuyNow,
    );
  }

  _handleDelegatedSubmit(e) {
    // No special form handling needed - let forms submit normally
  }

  _onGlobalSubmit(e) {
    const form = e.target;

    // Shopify product form - intercept and handle via AJAX
    if (
      form.matches('form[action="/cart/add"]') ||
      form.matches('form[action*="/cart/add"]')
    ) {
      e.preventDefault();
      this._handleFormSubmit(form);
      return;
    }

    // Shopify cart form (checkout) - allow default navigation
    if (
      form.matches('form[action="/cart"]') ||
      form.matches('form[action*="/cart"]')
    ) {
      // Don't intercept - let it navigate normally
      return;
    }
  }

  async _handleFormSubmit(form) {
    const formData = new FormData(form);
    const variantId = formData.get("id");
    const quantity = parseInt(formData.get("quantity"), 10) || 1;

    if (!variantId) {
      console.warn("No variant selected in form");
      return;
    }

    // Find product controller
    const productController = form.closest("product-controller");
    if (!productController) {
      console.warn("Form not inside product-controller");
      return;
    }

    // Check for buy now button that triggered submit
    const submitButton = form.querySelector(
      'button[type="submit"]:focus, button[data-buynow]',
    );
    const isBuyNow = submitButton?.dataset?.buynow === "true";

    // Use shared add to cart logic
    await this._performAddToCart(
      productController,
      variantId,
      quantity,
      isBuyNow,
    );
  }

  async _performAddToCart(productController, variantId, quantity, isBuyNow) {
    // Get all add to cart buttons in this product controller
    const allButtons = productController.querySelectorAll("[data-add-to-cart]");

    // Buy Now: skip AJAX add, redirect directly with variant permalink
    if (isBuyNow) {
      allButtons.forEach((btn) => {
        btn.disabled = true;
        if (btn.dataset.buynow === "true") {
          btn.dataset.state = "loading";
          btn.textContent =
            window.themeStrings?.cart?.redirecting || "Redirecting...";
        }
      });
      window.location.href = `/cart/${variantId}:${quantity}`;
      return;
    }

    // Set loading state for all buttons
    allButtons.forEach((btn) => {
      btn.disabled = true;
      btn.dataset.state = "loading";

      if (btn.dataset.buynow !== "true") {
        btn.textContent = window.themeStrings?.cart?.adding || "Adding...";
      }
    });

    try {
      const cart = await this._addToCart(variantId, quantity);

      // Success state - only for add to cart buttons
      if (!isBuyNow) {
        allButtons.forEach((btn) => {
          const btnIsBuyNow = btn.dataset.buynow === "true";
          if (!btnIsBuyNow) {
            btn.textContent = window.themeStrings?.cart?.added || "Added ✓";
          }
        });
      }

      // Emit updated event
      Shopify.emitEvent("cart:updated", {
        cart,
        count: cart.item_count,
        action: "add",
        variantId,
        buyNow: isBuyNow,
      });

      // Reset buttons after delay
      setTimeout(() => {
        allButtons.forEach((btn) => {
          const btnIsBuyNow = btn.dataset.buynow === "true";
          btn.dataset.state = "idle";
          btn.disabled = false;
          if (btnIsBuyNow) {
            btn.textContent = window.themeStrings?.cart?.buy_now || "Buy Now";
          } else {
            btn.textContent =
              window.themeStrings?.cart?.add_to_cart || "Add to Cart";
          }
        });
      }, 1500);

      // Open cart if setting is enabled (only for regular add to cart)
      if (!isBuyNow && window.themeSettings?.product?.openCartOnAdd) {
        document.dispatchEvent(new CustomEvent("cart:toggle"));
      }
    } catch (error) {
      console.error("Add to cart error:", error);

      // Set error state for all buttons
      allButtons.forEach((btn) => {
        btn.disabled = false;
        btn.textContent = window.themeStrings?.cart?.error || "Error";
      });

      Shopify.emitEvent("cart:error", {
        message: error.message,
        action: "add",
        variantId,
      });

      // Reset buttons after delay
      setTimeout(() => {
        allButtons.forEach((btn) => {
          const btnIsBuyNow = btn.dataset.buynow === "true";
          btn.dataset.state = "idle";
          if (btnIsBuyNow) {
            btn.textContent = window.themeStrings?.cart?.buy_now || "Buy Now";
          } else {
            btn.textContent =
              window.themeStrings?.cart?.add_to_cart || "Add to Cart";
          }
        });
      }, 2000);
    }
  }

  /* ===========================================
   * CART ACTIONS (from product controller, etc.)
   * =========================================== */

  async _onCartAction(e) {
    const { action, variantId, quantity = 1, buyNow = false } = e.detail;

    // Emit loading state
    Shopify.emitEvent("cart:loading", { action, variantId, buyNow });

    try {
      let cart;

      switch (action) {
        case "add":
          cart = await this._addToCart(variantId, quantity);
          break;
        case "increase":
        case "inc":
          cart = await this._changeQuantityByVariant(variantId, 1);
          break;
        case "decrease":
        case "dec":
          cart = await this._changeQuantityByVariant(variantId, -1);
          break;
        case "remove":
          cart = await this._removeByVariant(variantId);
          break;
        default:
          console.warn("Unknown cart action:", action);
          return;
      }

      // Emit success
      Shopify.emitEvent("cart:updated", {
        cart,
        count: cart.item_count,
        action,
        variantId,
        buyNow,
      });
    } catch (error) {
      console.error("Cart action error:", error);
      Shopify.emitEvent("cart:error", {
        message: error.message,
        action,
        variantId,
      });
    }
  }

  /* ===========================================
   * CART API METHODS
   * =========================================== */

  async _addToCart(variantId, quantity = 1) {
    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items: [{ id: parseInt(variantId, 10), quantity }],
      }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(
        data.description || data.message || "Failed to add to cart",
      );
    }

    // Fetch full cart to get updated state
    return this._fetchCart();
  }

  async _changeQuantityByVariant(variantId, delta) {
    const cart = await this._fetchCart();
    const item = cart.items.find(
      (i) => String(i.variant_id) === String(variantId),
    );

    if (!item) {
      throw new Error("Item not found in cart");
    }

    const newQuantity = Math.max(0, item.quantity + delta);
    return this._changeItemQuantity(item.key, newQuantity);
  }

  async _removeByVariant(variantId) {
    const cart = await this._fetchCart();
    const item = cart.items.find(
      (i) => String(i.variant_id) === String(variantId),
    );

    if (!item) {
      throw new Error("Item not found in cart");
    }

    return this._removeItem(item.key);
  }

  async _fetchCart() {
    const response = await fetch("/cart.js");

    if (!response.ok) {
      throw new Error("Failed to fetch cart");
    }

    return response.json();
  }

  /* ===========================================
   * ITEM QUANTITY MANAGEMENT
   * =========================================== */

  async _adjustQuantity(key, delta) {
    // Accumulate deltas and debounce the API call
    if (!this._pendingDeltas[key]) {
      this._pendingDeltas[key] = 0;
    }
    this._pendingDeltas[key] += delta;

    // Cancel any pending debounced call for this key
    if (this._quantityTimers[key]) {
      clearTimeout(this._quantityTimers[key]);
    }

    // Optimistic UI update so the displayed quantity changes immediately
    Shopify.emitEvent("cart:item-qty-optimistic", {
      key,
      delta,
    });

    this._quantityTimers[key] = setTimeout(async () => {
      const accumulatedDelta = this._pendingDeltas[key];
      delete this._pendingDeltas[key];
      delete this._quantityTimers[key];

      // Only show loading state when the actual API call fires
      Shopify.emitEvent("cart:item-loading", { key, isLoading: true });

      try {
        const cart = await this._fetchCart();
        const item = cart.items.find((i) => i.key === key);

        if (!item) {
          throw new Error("Item not found in cart");
        }

        const newQuantity = Math.max(0, item.quantity + accumulatedDelta);
        await this._changeItemQuantity(key, newQuantity);
      } catch (error) {
        console.error("Adjust quantity error:", error);
        Shopify.emitEvent("cart:item-loading", { key, isLoading: false });
      }
    }, 400);
  }

  _onQuantityInput(input) {
    // Get cart item container and key
    const itemContainer = input.closest("[data-cart-item-key]");
    if (!itemContainer) return;

    const key = itemContainer.dataset.cartItemKey;
    if (!key) return;

    const quantity = parseInt(input.value, 10);
    if (isNaN(quantity) || quantity < 0) return;

    // Debounce the API call
    if (this._quantityTimers[key]) {
      clearTimeout(this._quantityTimers[key]);
    }

    this._quantityTimers[key] = setTimeout(() => {
      this._changeItemQuantity(key, quantity);
      delete this._quantityTimers[key];
    }, 400);
  }

  async _changeItemQuantity(key, quantity) {
    Shopify.emitEvent("cart:item-loading", { key, isLoading: true });

    try {
      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: key, quantity }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Parse stock errors
        const errorMsg = this._parseStockError(data);
        throw new Error(errorMsg);
      }

      Shopify.emitEvent("cart:updated", {
        cart: data,
        count: data.item_count,
        action: "update",
        key,
      });
    } catch (error) {
      console.error("Change quantity error:", error);
      Shopify.emitEvent("cart:error", {
        message: error.message,
        action: "change-quantity",
        key,
      });

      // Reload cart to revert UI
      const cart = await this._fetchCart();
      Shopify.emitEvent("cart:updated", {
        cart,
        count: cart.item_count,
        action: "reload",
      });
    } finally {
      Shopify.emitEvent("cart:item-loading", { key, isLoading: false });
    }
  }

  /* ===========================================
   * ITEM REMOVAL
   * =========================================== */

  async _removeItem(key) {
    Shopify.emitEvent("cart:item-loading", { key, isLoading: true });

    try {
      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: key, quantity: 0 }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.description || data.message || "Failed to remove item",
        );
      }

      Shopify.emitEvent("cart:updated", {
        cart: data,
        count: data.item_count,
        action: "remove",
        key,
      });
    } catch (error) {
      console.error("Remove item error:", error);
      Shopify.emitEvent("cart:error", {
        message: error.message,
        action: "remove",
        key,
      });
    } finally {
      Shopify.emitEvent("cart:item-loading", { key, isLoading: false });
    }
  }

  /* ===========================================
   * CART NOTE
   * =========================================== */

  _onNoteInput(textarea) {
    const note = textarea.value;

    // Debounce note saving
    if (this._noteTimer) {
      clearTimeout(this._noteTimer);
    }

    this._noteTimer = setTimeout(() => {
      this._saveNote(note);
    }, 800);
  }

  async _saveNote(note) {
    Shopify.emitEvent("cart:note-saving");

    try {
      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ note }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.description || data.message || "Failed to save note",
        );
      }

      Shopify.emitEvent("cart:note-saved", { cart: data });
      Shopify.emitEvent("cart:updated", {
        cart: data,
        count: data.item_count,
        action: "note",
      });
    } catch (error) {
      console.error("Save note error:", error);
      Shopify.emitEvent("cart:error", {
        message: error.message,
        action: "save-note",
      });
    }
  }

  /* ===========================================
   * ERROR PARSING
   * =========================================== */

  _parseStockError(data) {
    // Check for description first (Shopify's primary error field)
    if (data.description) {
      return data.description;
    }

    // Parse stock limitation messages
    if (data.message) {
      const stockMatch = data.message.match(/only (\d+)/i);

      if (stockMatch) {
        const qty = stockMatch[1];
        return (
          window.themeStrings?.cart?.stock_error?.replace("{quantity}", qty) ||
          `Only ${qty} available in stock`
        );
      }

      if (
        data.message.toLowerCase().includes("stock") ||
        data.message.toLowerCase().includes("available")
      ) {
        return (
          window.themeStrings?.cart?.out_of_stock ||
          "This item is currently out of stock"
        );
      }

      return data.message;
    }

    return window.themeStrings?.cart?.update_error || "Failed to update cart";
  }
}

customElements.define("cart-controller", CartController);
